import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Table,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import { toast, Bounce } from "react-toastify";
import moreIcon from "../../../assets/images/more.png";
import { get, put, post, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";

const RolesPermissions = () => {
  const [newRole, setNewRole] = useState("");
  const [newPermission, setNewPermission] = useState("");
  const [roles, setRoles] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [loading, setLoading] = useState(null);

  const toggleDropdown = (roleId) => {
    setDropdownOpen((prevState) => (prevState === roleId ? null : roleId));
  };

  // Mock data initialization
  useEffect(() => {
    const mockPermissions = {
      gym: ["viewGym", "createGym", "updateGym", "deleteGym"],
      staff: ["viewStaff", "createStaff", "updateStaff", "deleteStaff"],
      user: ["viewUser", "updateUser", "deleteUser"],
      passes: ["viewPass", "createPass", "updatePass", "deletePass"],
    };

    setPermissionsList(mockPermissions);
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await get(endpoints.roles);

        if (response.data) {
          setRoles(response.data.data);
        } else {
          toast.error("Failed to fetch roles", {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          });
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        toast.error("Error fetching roles", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
    };

    fetchRoles();
  }, []);

  const handleAddRole = (e) => {
    e.preventDefault();
    if (newRole.trim()) {
      const newRoleObject = {
        id: roles.length + 1,
        name: newRole,
        permissions: [],
      };
      setRoles((prevRoles) => [...prevRoles, newRoleObject]);
      setNewRole("");
      setIsModalOpen(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await del(`${endpoints.roles}/${roleId}`);
      setRoles((prevRoles) => prevRoles.filter((role) => role.id !== roleId));
      toast.success("Role Deleted Successfully", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const formatPermissionName = (permission) => {
    return permission
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleCheckboxChange = (roleId, permission) => {
    setRoles((prevRoles) =>
      prevRoles.map((role) =>
        role.id === roleId
          ? {
              ...role,
              permissions: role.permissions.includes(permission)
                ? role.permissions.filter((p) => p !== permission)
                : [...role.permissions, permission],
            }
          : role
      )
    );
  };

  const handleUpdatePermissions = async () => {
    const userList = roles.map((role) => ({
      name: role.name,
      permissions: role.permissions,
    }));

    try {
      const response = await post(endpoints.roles, { userList: userList });
      if (response.data && response.data.success) {
        toast.success("Permissions Updated Successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      } else {
        console.error("Failed to update permissions");
      }
    } catch (error) {
      console.error("Error updating permissions", error);
    }
  };

  return (
    <>
      <Card className="rounded-4" style={{ height: "550px" }}>
        <CardBody>
          <span className="font-size-20 ff-sora fw-bold primary-black">
            Roles & Permissions
          </span>
          <SimpleBar style={{ height: "360px" }}>
            {permissionsList.length > 0 ? (
              <Table className="table-responsive hide-scrollbar table-borderless table-centered table-nowrap">
                <thead>
                  <tr>
                    <th className="fixed-column ff-sora fw-normal font-size-16 secondary-black">
                      Permissions
                    </th>
                    {roles.map((role, index) => (
                      <th
                        key={role.id + index}
                        className="ff-sora fw-normal font-size-16 secondary-black"
                      >
                        <div className="d-flex justify-content-between align-items-center secondary-black text-capitalize">
                          {role.name}
                          <Dropdown
                            isOpen={dropdownOpen === role.id}
                            toggle={() => toggleDropdown(role.id)}
                            className="ms-2"
                          >
                            <DropdownToggle
                              tag="span"
                              caret
                              className="cursor-pointer secondary-black"
                            >
                              <img src={moreIcon} className="cursor-pointer" />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                onClick={() => handleDeleteRole(role.id)}
                                className="text-danger"
                              >
                                Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </th>
                    ))}
                    <th className="ff-sora fw-bold font-size-16 button-black">
                      <Button
                        className="rounded-pill"
                        onClick={() => setIsModalOpen(true)}
                      >
                        +Add Role
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody style={{ minHeight: "550px" }}>
                  {permissionsList.map((permission, index) => (
                    <tr key={index}>
                      <td className="fixed-column">
                        <h6 className="font-size-16 ff-sora light-black mb-1 fw-normal">
                          {formatPermissionName(permission)}
                        </h6>
                      </td>
                      {roles.map((role, index) => (
                        <td key={role.id + index}>
                          <Input
                            type="checkbox"
                            checked={role.permissions.includes(permission)}
                            onChange={() =>
                              handleCheckboxChange(role.id, permission)
                            }
                            className="secondary-black"
                            style={{
                              borderColor: "#525252",
                              width: "20px",
                              height: "20px",
                            }}
                          />
                        </td>
                      ))}
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p font-size-16 ff-sora secondary-black fw-normal>
                No roles or permissions found.
              </p>
            )}
          </SimpleBar>
          <Button
            className="mt-3 float-end rounded-pill"
            onClick={handleUpdatePermissions}
          >
            Add Permissions
          </Button>
        </CardBody>

        {/* Modal for adding a new role */}
        <Modal
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen(!isModalOpen)}
          className="modal-dialog-centered rounded-4"
        >
          <ModalHeader
            toggle={() => setIsModalOpen(!isModalOpen)}
            className="ff-sora"
          >
            Add New Role
          </ModalHeader>
          <form onSubmit={handleAddRole}>
            <ModalBody>
              <Input
                type="text"
                placeholder="Enter Role Name"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              />
            </ModalBody>
            <style>
              {`
            input {
  width: 80%;
  height: 50px;
  padding: 10px;
  font-family: 'Sora', sans-serif; 
  font-size: 14px; 
  font-weight: 400; 
  color: black; 
  background-color: white;
  border: 1px solid #525252;
  border-radius: 8px;
  outline: #525252;
}
    input::placeholder {
      color: #525252;
      font-size: 14px;
      font-weight: 400;
       font-family: 'Sora', sans-serif; 
    }
       input:focus {
   
  height: 50px;
  padding: 10px;
  font-family: 'Sora', sans-serif; 
  font-size: 14px; 
  font-weight: 400; 
  color: black; 
  background-color: white;
  border: 1px solid #525252 !important;
  border-radius: 8px;
  outline: #525252;
}
  `}
            </style>
            <ModalFooter>
              <Button
                color="white"
                className="rounded-pill1"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className=" me-2 rounded-pill">
                Add Role
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </Card>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          height: "60px",
          position: "sticky",
          bottom: "0",
          zIndex: "10",
        }}
      ></div>
    </>
  );
};

export default RolesPermissions;
